/* Ralph Karouni PDP behaviour (spec: docs/graff-analysis/graff-pdp-spec.md).
   1) keeps --rk-header-h synced (same pattern as rk-collection.js) so the first
      screen sizes to the live header;
   2) gallery + recommendations carousels: scroll-snap tracks, arrows page by one
      item, disabled state at the ends, RTL-aware;
   3) zoom lightbox via native <dialog>;
   4) accordion rows (grid-template-rows animation via .is-open);
   5) desktop sticky mini-ATB bar after the first screen;
   6) wishlist hearts are handled by assets/rk-wishlist.js. */
(function () {
  'use strict';

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function headerEl() {
    return (
      document.querySelector('.header-wrapper') ||
      document.querySelector('.section-header') ||
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('.header')
    );
  }

  var lastHeaderH = null;
  function syncHeaderVar() {
    var h = headerEl();
    var b = h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 110;
    if (b !== lastHeaderH) {
      lastHeaderH = b;
      document.documentElement.style.setProperty('--rk-header-h', b + 'px');
    }
  }

  function initCarousel(track, prevBtn, nextBtn, itemSelector, gap) {
    if (!track || !prevBtn || !nextBtn) return;
    var rtl = document.documentElement.dir === 'rtl';

    function step() {
      var item = track.querySelector(itemSelector);
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    }
    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }
    function pos() {
      return Math.abs(track.scrollLeft);
    }
    function update() {
      var m = maxScroll();
      prevBtn.disabled = pos() < 4;
      nextBtn.disabled = pos() > m - 4 || m <= 0;
    }
    function go(dir) {
      var amount = step() * dir * (rtl ? -1 : 1);
      track.scrollBy({ left: amount, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }
    prevBtn.addEventListener('click', function () { go(-1); });
    nextBtn.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function init(root) {
    syncHeaderVar();

    // Gallery
    initCarousel(
      root.querySelector('[data-rk-pdp-track]'),
      root.querySelector('[data-rk-pdp-prev]'),
      root.querySelector('[data-rk-pdp-next]'),
      '[data-rk-pdp-slide]',
      window.matchMedia('(min-width: 990px)').matches ? 30 : 14
    );

    // Recommendations
    initCarousel(
      root.querySelector('[data-rk-pdp-recs-track]'),
      root.querySelector('[data-rk-pdp-recs-prev]'),
      root.querySelector('[data-rk-pdp-recs-next]'),
      '.rk-pdp__rec',
      window.matchMedia('(min-width: 990px)').matches ? 30 : 14
    );

    // Zoom lightbox = a magnifier that never goes soft.
    // The image is laid out at the ORIGINAL's pixel size and scaled with a transform.
    // Zoom runs from "fit the screen" up to exactly one photo pixel per screen pixel
    // (scale = 1 / devicePixelRatio) and no further: past that point there is no more
    // detail in the file, so any extra magnification could only blur it.
    var lightbox = root.querySelector('[data-rk-pdp-lightbox]');
    var lightboxImg = root.querySelector('[data-rk-pdp-lightbox-img]');
    var stage = root.querySelector('[data-rk-pdp-zoom-stage]');
    var hint = root.querySelector('[data-rk-pdp-zoom-hint]');
    if (lightbox && lightboxImg && stage && typeof lightbox.showModal === 'function') {
      var z = { w: 0, h: 0, fit: 1, max: 1, s: 1, tx: 0, ty: 0 };
      var coarse = window.matchMedia('(pointer: coarse)').matches;
      var pointers = {};
      var gesture = null;
      var loadToken = 0;

      var zoomable = function () { return z.max > z.fit * 1.02; };
      var apply = function () {
        lightboxImg.style.transform = 'translate(' + z.tx + 'px,' + z.ty + 'px) scale(' + z.s + ')';
        var zoomed = z.s > z.fit * 1.01;
        stage.classList.toggle('is-zoomed', zoomed);
        stage.classList.toggle('is-zoomable', zoomable() && !zoomed);
      };
      var clamp = function () {
        var sw = stage.clientWidth, sh = stage.clientHeight;
        var iw = z.w * z.s, ih = z.h * z.s;
        z.tx = iw <= sw ? (sw - iw) / 2 : Math.min(0, Math.max(sw - iw, z.tx));
        z.ty = ih <= sh ? (sh - ih) / 2 : Math.min(0, Math.max(sh - ih, z.ty));
      };
      var fitNow = function () {
        var sw = stage.clientWidth, sh = stage.clientHeight;
        if (!z.w || !sw) return;
        z.fit = Math.min(sw / z.w, sh / z.h);
        z.max = Math.max(z.fit, 1 / (window.devicePixelRatio || 1));
        z.s = z.fit;
        clamp();
        apply();
      };
      var zoomTo = function (s, cx, cy) {
        s = Math.min(z.max, Math.max(z.fit, s));
        z.tx = cx - (cx - z.tx) * (s / z.s);
        z.ty = cy - (cy - z.ty) * (s / z.s);
        z.s = s;
        clamp();
        apply();
      };
      var local = function (e) {
        var r = stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };
      var dismissHint = function () { if (hint) hint.classList.add('is-hidden'); };

      root.querySelectorAll('[data-rk-pdp-zoom]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var token = ++loadToken;
          z.w = parseInt(btn.getAttribute('data-w'), 10) || 2000;
          z.h = parseInt(btn.getAttribute('data-h'), 10) || 2000;
          lightboxImg.style.width = z.w + 'px';
          lightboxImg.style.height = z.h + 'px';
          lightboxImg.alt = btn.getAttribute('data-alt') || '';
          // The screen-size file paints first; the full original swaps in once it has
          // downloaded. Both are laid out at the original's size, so nothing jumps.
          lightboxImg.src = btn.getAttribute('data-full') || '';
          var orig = btn.getAttribute('data-orig');
          if (orig) {
            var pre = new Image();
            pre.onload = function () { if (token === loadToken) lightboxImg.src = orig; };
            pre.src = orig;
          }
          if (hint) {
            hint.textContent = hint.getAttribute(coarse ? 'data-touch' : 'data-mouse') || '';
            hint.classList.remove('is-hidden');
          }
          lightbox.showModal();
          requestAnimationFrame(fitNow);
        });
      });

      // Pointer handling: one pointer drags, two pointers pinch; a press that barely
      // moves is a tap/click and toggles between fit and full detail.
      stage.addEventListener('pointerdown', function (e) {
        stage.setPointerCapture(e.pointerId);
        pointers[e.pointerId] = local(e);
        var ids = Object.keys(pointers);
        if (ids.length === 1) {
          gesture = { type: 'pan', start: local(e), tx: z.tx, ty: z.ty, moved: 0 };
        } else if (ids.length === 2) {
          var a = pointers[ids[0]], b = pointers[ids[1]];
          gesture = {
            type: 'pinch',
            dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
            s: z.s,
            mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
            tx: z.tx, ty: z.ty
          };
        }
        stage.classList.add('is-grabbing');
      });
      stage.addEventListener('pointermove', function (e) {
        if (!pointers[e.pointerId] || !gesture) return;
        pointers[e.pointerId] = local(e);
        var ids = Object.keys(pointers);
        if (gesture.type === 'pan' && ids.length === 1) {
          var p = pointers[ids[0]];
          var dx = p.x - gesture.start.x, dy = p.y - gesture.start.y;
          gesture.moved = Math.max(gesture.moved, Math.abs(dx) + Math.abs(dy));
          if (z.s > z.fit * 1.01) {
            z.tx = gesture.tx + dx;
            z.ty = gesture.ty + dy;
            clamp();
            apply();
          }
        } else if (gesture.type === 'pinch' && ids.length >= 2) {
          var a = pointers[ids[0]], b = pointers[ids[1]];
          var mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          var s = Math.min(z.max, Math.max(z.fit, gesture.s * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.dist)));
          // Keep the photo point that started under the fingers under the fingers.
          z.tx = mid.x - (gesture.mid.x - gesture.tx) * (s / gesture.s);
          z.ty = mid.y - (gesture.mid.y - gesture.ty) * (s / gesture.s);
          z.s = s;
          clamp();
          apply();
          dismissHint();
        }
      });
      var endPointer = function (e) {
        if (!pointers[e.pointerId]) return;
        var p = pointers[e.pointerId];
        delete pointers[e.pointerId];
        var left = Object.keys(pointers).length;
        if (gesture && gesture.type === 'pan' && left === 0 && gesture.moved < 6 && e.type === 'pointerup' && zoomable()) {
          if (z.s > z.fit * 1.01) zoomTo(z.fit, p.x, p.y);
          else zoomTo(z.max, p.x, p.y);
          dismissHint();
        } else if (gesture && gesture.moved >= 6) {
          dismissHint();
        }
        if (left === 1) {
          var id = Object.keys(pointers)[0];
          gesture = { type: 'pan', start: pointers[id], tx: z.tx, ty: z.ty, moved: 99 };
        } else if (left === 0) {
          gesture = null;
          stage.classList.remove('is-grabbing');
        }
      };
      stage.addEventListener('pointerup', endPointer);
      stage.addEventListener('pointercancel', endPointer);

      stage.addEventListener('wheel', function (e) {
        if (!zoomable()) return;
        e.preventDefault();
        var p = local(e);
        zoomTo(z.s * Math.exp(-e.deltaY * 0.0022), p.x, p.y);
        dismissHint();
      }, { passive: false });

      window.addEventListener('resize', function () { if (lightbox.open) fitNow(); });

      var close = root.querySelector('[data-rk-pdp-lightbox-close]');
      if (close) close.addEventListener('click', function () { lightbox.close(); });
      lightbox.addEventListener('close', function () {
        loadToken++;
        pointers = {};
        gesture = null;
        lightboxImg.removeAttribute('src');
        stage.classList.remove('is-zoomed', 'is-zoomable', 'is-grabbing');
      });
    }

    // Accordions
    root.querySelectorAll('[data-rk-pdp-acc-toggle]').forEach(function (head) {
      head.addEventListener('click', function () {
        var item = head.closest('.rk-pdp__acc-item');
        var open = item.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    // Wishlist hearts are owned by assets/rk-wishlist.js, which persists the
    // saved pieces per signed-in customer and keeps both PDP hearts in sync.

    // Sticky mini bar: activates once the actions bar scrolls above the viewport top
    var sticky = root.querySelector('[data-rk-pdp-sticky]');
    var bar = root.querySelector('.rk-pdp__actions');
    if (sticky && bar) {
      /* The gate used to be min-width:990px, which switched the bar off on phones and
         tablets. The CSS now shows the bar at every width (as a bottom bar below 990),
         so the only thing left to decide is whether the buy row has scrolled past. */
      var ticking = false;
      function apply() {
        ticking = false;
        var past = bar.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle('m-active', past);
        sticky.toggleAttribute('inert', !past); /* inert (not aria-hidden) because the bar holds focusable controls */
        sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
      }
      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(apply);
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      apply();
    }

    window.addEventListener('resize', syncHeaderVar);
    window.addEventListener('load', syncHeaderVar);
  }

  function boot() {
    var root = document.querySelector('[data-rk-pdp]');
    if (root && !root.dataset.rkPdpInit) {
      root.dataset.rkPdpInit = '1';
      init(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function () {
    var root = document.querySelector('[data-rk-pdp]');
    if (root) {
      delete root.dataset.rkPdpInit;
      boot();
    }
  });
})();
